import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import type { Thing } from 'schema-dts';
import { getCommonPageProps, getNews, getPostCategories } from '@/shared/api';
import { getCommunityEvents } from '@/shared/api/community-events';
import { ApiPageSlug, getPageDetail } from '@/shared/api/page-detail';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Link from '@/shared/components/shared/Link';
import { CommonPageProps } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const SitemapBlock = ({ list }: { list: AppSitemapBlock[] }) => {
    return list.length > 0 ? (
        <ul className="ul sitemap-block">
            {list.map((block) => (
                <li key={block.href + block.title} className="sitemap-block__item">
                    {block.href ? (
                        <Link href={block.href} className="link">
                            {block.title}
                        </Link>
                    ) : (
                        <span className="sitemap-block__item-el">{block.title}</span>
                    )}
                    {block.sublist.length > 0 ? <SitemapBlock list={block.sublist} /> : null}
                </li>
            ))}
        </ul>
    ) : null;
};

const SitemapPage = ({ tree, breadcrumbs }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    return (
        <div className="wrapper sitemap-page-content js-header-theme-trigger" data-theme="dark">
            <div className="wrapper-inner">
                <h1 className="h2 sitemap-page-content__title">Карта сайта</h1>
                {tree.length > 0 ? <SitemapBlock list={tree} /> : null}
            </div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-sitemap"
                data={generateWebPageJsonLd({
                    name: 'Карта сайта',
                    pagePath: '/sitemap/',
                    breadcrumbs,
                })}
            />
        </div>
    );
};

export default SitemapPage;

type AppSitemapBlock = {
    title: string;
    href?: string;
    sublist: AppSitemapBlock[];
};

type ErrorPageProps = CommonPageProps & {
    tree: AppSitemapBlock[];
};

export const getServerSideProps: GetServerSideProps<ErrorPageProps> = async () => {
    const pageSlugs: ApiPageSlug[] = [
        'participants',
        'partners',
        'visitors',
        'expo',
        'technological',
        'hr',
        'program',
        'new-program',
        'program-2026',
        'events',
        'posts',
        'registration',
        'contacts',
        'hotels',
        'car-rent',
        'car-accreditation',
        'shuttles',
        'food-areas',
    ];

    const [
        commonPageProps,
        [
            _participantsPageResponse,
            partnersPageResponse,
            _visitorsPageResponse,
            _expoPageResponse,
            technologicalPageResponse,
            hrPageResponse,
            programPageResponse,
            eventsPageResponse,
            _newsPageResponse,
            _registrationPageResponse,
            contactsPageResponse,
            hotelsPageResponse,
            carRentPageResponse,
            carAccreditationPageResponse,
            shuttlesPageResponse,
            foodAreasPageResponse,
        ],
        communityEventsResponse,
        [categoriesResponse, newsResponse],
    ] = await Promise.all([
        getCommonPageProps(),
        Promise.all(pageSlugs.map((slug) => getPageDetail({ params: { slug } }))),
        getCommunityEvents({ params: { limit: 9999 } }),
        getPostCategories().then((categoriesResponse) =>
            Promise.all([
                categoriesResponse,
                getNews({ params: { limit: 9999 }, postCategories: categoriesResponse.data ?? [] }),
            ]),
        ),
    ]);

    const sitemapTree: AppSitemapBlock[] = [
        {
            title: eventsPageResponse.data?.meta?.title || 'Календарь мероприятий',
            href: '/community/',
            sublist: (communityEventsResponse.data?.items ?? []).map((item) => ({
                title: item.title,
                href: `/community/${item.slug}/`,
                sublist: [],
            })),
        },
        { title: hrPageResponse.data?.meta?.title || 'События форума', href: '/events/summit/', sublist: [] },
        {
            title: technologicalPageResponse.data?.meta?.title || 'События форума',
            href: '/events/technological/',
            sublist: [],
        },
        {
            title: 'Новости',
            href: '/news/',
            sublist: (categoriesResponse.data ?? []).map((category) => ({
                title: category.name,
                href: `/news/${category.slug}/`,
                sublist: (newsResponse.data?.items ?? [])
                    .filter((newsItem) => newsItem.categories.find((c) => c.slug === category.slug))
                    .map((newsItem) => ({
                        title: newsItem.title,
                        href: `/news/${category.slug}/${newsItem.slug}/`,
                        sublist: [],
                    })),
            })),
        },
        {
            title: 'FORUM',
            href: '/participation/participants/',
            sublist: [],
        },
        {
            title: partnersPageResponse.data?.meta?.title || 'Партнеры',
            href: '/participation/partners/',
            sublist: [],
        },
        {
            title: 'EXPO',
            href: '/participation/visitors/',
            sublist: [],
        },
        {
            title: carAccreditationPageResponse.data?.meta?.title || 'Услуги',
            href: '/services/car-accreditation/',
            sublist: [],
        },
        { title: carRentPageResponse.data?.meta?.title || 'Услуги', href: '/services/car-rent/', sublist: [] },
        { title: foodAreasPageResponse.data?.meta?.title || 'Услуги', href: '/services/food-areas/', sublist: [] },
        { title: hotelsPageResponse.data?.meta?.title || 'Услуги', href: '/services/hotels/', sublist: [] },
        { title: shuttlesPageResponse.data?.meta?.title || 'Услуги', href: '/services/shuttles/', sublist: [] },
        { title: contactsPageResponse.data?.meta?.title || 'Контакты', href: '/contacts/', sublist: [] },
        { title: 'Выставка', href: '/expo/', sublist: [] },
        { title: 'Политика конфиденциальности', href: '/privacy', sublist: [] },
        { title: 'Согласие на обработку персональных данных', href: '/agreement/', sublist: [] },
        { title: programPageResponse.data?.meta?.title || 'Программа 2025', href: '/program-2025/', sublist: [] },
        { title: programPageResponse.data?.meta?.title || 'Программа', href: '/program/', sublist: [] },
        { title: 'Приложение TNF', href: '/application/', sublist: [] },
        { title: 'Регистрация', href: '/registration/', sublist: [] },
    ];

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'sitemap-page',
            meta: {
                ...commonPageProps.meta,
                title: 'Карта сайта',
            },
            tree: sitemapTree,
        },
    };
};
